import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getBitacora } from "../../../../../apis";
import { parseDateToLgComun4 } from "../../../../../helpers/dateTransform";
import {
  alpha,
  Box,
  CardMedia,
  Chip,
  Collapse,
  Fade,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { TableSkeleton } from "../../../../Loading/Table/Table";
import { EGPaginator } from "../../../../Paginator/Paginator";
import Etiquin from "../../../../../assets/images/Ethiquin/ethiquin_no_data.svg";
import { UserTooltipChip } from "./UserTooltipChip";
import { ArrowDown2, OutlineEye } from "../../../../Icon";

/**
 * Translate HTTP methods to human-readable Spanish terms
 */
const translateMethod = (method, t) => {
  const methodUpper = method?.toUpperCase();
  const translations = {
    GET: t("Bitacora.view_action"),
    POST: t("Bitacora.modification_action"),
    PUT: t("Bitacora.modification_action"),
    PATCH: t("Bitacora.modification_action"),
    DELETE: t("Bitacora.deletion_action"),
  };
  return translations[methodUpper] || method;
};

/**
 * Helper function to format actions object into readable components
 * Returns an array of objects with label and value
 */
const formatActions = (actions, t) => {
  if (!actions || typeof actions !== "object") return [];

  const formatted = [];

  // Fields to exclude from display
  const excludedFields = ["method", "status", "report_id", "solicitud", "user_id", "data", "page", "page_size", "folio", "tracking_code", "changes", "checkboxes", "comment", "message"];

  // Method (PUT, GET, POST, DELETE)
  if (actions.method) {
    formatted.push({
      label: t("Bitacora.method"),
      value: translateMethod(actions.method, t),
      color: getMethodColor(actions.method),
      isMethod: true,
    });
  }

  // Status
  if (actions.status) {
    formatted.push({
      label: t("Bitacora.status_action"),
      value: t(`status.${actions.status}`) || actions.status,
      color: "primary",
    });
  }

  // Solicitud (GET, POST, etc.)
  if (actions.solicitud) {
    formatted.push({
      label: t("Bitacora.request"),
      value: translateMethod(actions.solicitud, t),
      color: getMethodColor(actions.solicitud),
    });
  }

  // User ID - special handling for tooltip (check both locations)
  const userId = actions.user_id || actions.data?.user_id;
  if (userId) {
    formatted.push({
      label: t("Bitacora.user_id"),
      value: String(userId),
      color: "secondary",
      isUserId: true,
      userId: userId,
    });
  }

  // Handle nested data object (new format)
  if (actions.data && typeof actions.data === "object") {
    Object.keys(actions.data).forEach((key) => {
      // Exclude user_id since it's handled above with tooltip
      if (!["report_id", "page", "page_size", "folio", "user_id", "tracking_code", "changes"].includes(key)) {
        const value = actions.data[key];
        // Skip empty values
        if (value === "" || value === null || value === undefined) return;
        // Skip numeric values
        if (typeof value === "number") return;
        // Handle boolean values with translation
        if (typeof value === "boolean") {
          formatted.push({
            label: key,
            value: value ? t("Bitacora.enabled") : t("Bitacora.disabled"),
            color: value ? "success" : "default",
          });
        // Handle nested objects within data
        } else if (typeof value === "object" && value !== null) {
          formatted.push({
            label: key,
            value: JSON.stringify(value),
            color: "default",
          });
        } else {
          formatted.push({
            label: key,
            value: String(value),
            color: "default",
          });
        }
      }
    });
  }

  // Checkboxes - show only the ones with checked === true
  if (Array.isArray(actions.checkboxes)) {
    actions.checkboxes
      .filter((cb) => cb && cb.checked === true)
      .forEach((cb, idx) => {
        formatted.push({
          label: `checkbox_${idx}`,
          value: cb.key,
          color: "primary",
        });
      });
  }

  // Any other fields in actions (excluding nested objects)
  Object.keys(actions).forEach((key) => {
    if (!excludedFields.includes(key)) {
      const value = actions[key];
      if (typeof value !== "object") {
        formatted.push({
          label: key,
          value: String(value),
          color: "default",
        });
      }
    }
  });

  return formatted;
};

/**
 * Get color for HTTP method
 */
const getMethodColor = (method) => {
  const colors = {
    GET: "info",
    POST: "success",
    PUT: "warning",
    PATCH: "warning",
    DELETE: "error",
  };
  return colors[method?.toUpperCase()] || "default";
};

const isPrimitive = (v) =>
  v === null ||
  v === undefined ||
  v === "" ||
  typeof v === "boolean" ||
  typeof v === "string" ||
  typeof v === "number";

const isInlineable = (v) =>
  isPrimitive(v) ||
  (Array.isArray(v) && v.every((it) => it === null || typeof it !== "object"));

const PRIORITY_VALUES = new Set(["low", "medium", "hight"]);

const formatLeafValue = (value, fieldName, t) => {
  if (fieldName === "priority" && PRIORITY_VALUES.has(value)) {
    return t(`Bitacora.priority.${value}`);
  }
  return String(value);
};

/**
 * Renders a before/after change value with proper formatting based on its type.
 * Objects are rendered as indented key: value lists (never raw JSON).
 * `fieldName` is the key/path tail of the value being rendered, used for
 * value-level i18n (e.g. translating priority enums).
 */
const ChangeValue = ({ value, fieldName }) => {
  const { t } = useTranslation("report");

  if (value === null || value === undefined || value === "") {
    return (
      <Typography
        component="span"
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: "italic", fontSize: 12 }}
      >
        —
      </Typography>
    );
  }

  if (typeof value === "boolean") {
    return (
      <Chip
        size="small"
        label={value ? t("Bitacora.enabled") : t("Bitacora.disabled")}
        color={value ? "success" : "default"}
        sx={{ fontSize: 11, height: 22 }}
      />
    );
  }

  if (typeof value === "string" || typeof value === "number") {
    return (
      <Typography
        component="span"
        variant="body2"
        sx={{ fontSize: 12, wordBreak: "break-word" }}
      >
        {formatLeafValue(value, fieldName, t)}
      </Typography>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic", fontSize: 12 }}
        >
          [ ]
        </Typography>
      );
    }
    const allPrimitive = value.every((it) => it === null || typeof it !== "object");
    if (allPrimitive) {
      return (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ display: "inline-flex" }}
        >
          {value.map((item, i) => (
            <Chip
              key={i}
              size="small"
              label={item === null ? "null" : formatLeafValue(item, fieldName, t)}
              sx={{ fontSize: 10, height: 20 }}
            />
          ))}
        </Stack>
      );
    }
    return (
      <Stack spacing={0.5}>
        {value.map((item, i) => (
          <Box key={i}>
            <Typography
              component="div"
              variant="body2"
              sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}
            >
              [{i}]
            </Typography>
            <Box
              sx={{
                pl: 1.25,
                ml: 0.5,
                borderLeft: "2px solid",
                borderColor: "divider",
              }}
            >
              <ChangeValue value={item} fieldName={fieldName} />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  const entries = Object.entries(value);
  if (entries.length === 0) {
    return (
      <Typography
        component="span"
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: "italic", fontSize: 12 }}
      >
        {"{ }"}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {entries.map(([k, v]) => {
        const inline = isInlineable(v);
        return (
          <Box key={k}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: 12, fontWeight: 600 }}
              >
                {k}:
              </Typography>
              {inline && <ChangeValue value={v} fieldName={k} />}
            </Stack>
            {!inline && (
              <Box
                sx={{
                  pl: 1.25,
                  ml: 0.5,
                  mt: 0.25,
                  borderLeft: "2px solid",
                  borderColor: "divider",
                }}
              >
                <ChangeValue value={v} fieldName={k} />
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * ReportBitacora Component
 * Displays the bitacora (log) table filtered by a specific folio
 * Used in the Report Information tab to show logs related to the current report
 */
export const ReportBitacora = ({ folio, reportId }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation("report");
  const currentLang = i18n.language;

  // ---> Initial states <--- //
  const [isLoading, setIsLoading] = useState(true);
  const [bitacoraList, setBitacoraList] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  // Server pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const toggleRow = (id) =>
    setExpandedRow((prev) => (prev === id ? null : id));

  // Calculation of total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Table columns (without folio, ID, and params)
  const columns = useMemo(
    () => [
      { id: "event", label: t("Bitacora.event"), align: "left", minWidth: 200 },
      { id: "user", label: t("Bitacora.user"), align: "left", minWidth: 180 },
      { id: "date", label: t("Bitacora.date"), align: "left", minWidth: 200 },
      { id: "details", label: t("Bitacora.details"), align: "left", minWidth: 250 },
    ],
    [t]
  );

  // Table rows
  const rows = useMemo(() => {
    if (!Array.isArray(bitacoraList)) return [];
    return bitacoraList.map((b) => {
      const isInvestigationStatus =
        b.path === "casemanager.report.status.edit" &&
        Array.isArray(b.actions?.checkboxes);
      return {
        id: b.id,
        event: isInvestigationStatus
          ? t("Bitacora.investigation_status")
          : t(`Rutas.${b.path}`),
        details: formatActions(b.actions, t),
        changes: Array.isArray(b.actions?.changes) ? b.actions.changes : [],
        user: b.user,
        date: parseDateToLgComun4(b.created_at, currentLang),
      };
    });
  }, [bitacoraList, t, currentLang]);

  const hasRows = rows.length > 0;
  const hasMoreThan10Rows = rows.length > 15;

  // API call filtered by folio
  const fetchBitacora = useCallback(
    async (pageParam = page, sizeParam = rowsPerPage) => {
      if (!folio) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const params = {
          folio: folio,
          report_id: reportId,
          page: pageParam,
          page_size: sizeParam,
        };

        const data = await getBitacora(params);

        setBitacoraList(data?.results ?? []);
        setTotalCount(data?.count ?? 0);
      } catch (error) {
        console.error("[ReportBitacora] fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [folio, reportId, page, rowsPerPage]
  );

  // Fetch when folio, page or rowsPerPage changes
  useEffect(() => {
    fetchBitacora(page, rowsPerPage);
  }, [fetchBitacora, page, rowsPerPage]);

  // Page onChange event
  const handlePageChange = (_evt, newPage) => {
    setPage(newPage);
  };

  // Rows per page onChange event
  const handleRowsPerPageChange = (evt) => {
    const newSize = Number(evt.target.value);
    setRowsPerPage(newSize);
    setPage(1); // Reset to first page
  };

  // Don't render if no folio is provided
  if (!folio) {
    return null;
  }

  return (
    <Box className="mt-20 dyTheme1 dyBorder1 rounded">
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: theme.palette.blueGray50.main,
          py: 1.5,
          px: 2,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <Typography variant="h6" color="secondary">
          {t("Bitacora.title")}
        </Typography>
        <Typography variant="body2" color="secondary">
          {`${t("ReportsList.showing")}: ${totalCount} ${t("Bitacora.result")}${
            totalCount === 1 ? "" : "s"
          }`}
        </Typography>
      </Box>

      {/* Table + Paginator */}
      <Box sx={{ p: 1.5 }}>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Fade in timeout={1000}>
            <Box>
              <TableContainer
                sx={{
                  height: "50vh",
                  overflowY: "auto",
                  overflowX: { xs: "auto", xl: "hidden" },
                  border: `1px solid ${alpha(theme.palette.blueGray50.main, 0.3)}`,
                  borderTopLeftRadius: "6px",
                  borderTopRightRadius: "6px",
                }}
              >
                {!hasRows ? (
                  <Stack
                    sx={{
                      height: "40vh",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={Etiquin}
                      alt="not_found"
                      sx={{ height: "45%", width: "45%", objectFit: "contain" }}
                    />
                    <Typography
                      variant="h5"
                      textAlign="center"
                      color="secondary"
                      mt={1.5}
                    >
                      {t("ReportsList.no_results_found")}...
                    </Typography>
                  </Stack>
                ) : (
                  <Table
                    stickyHeader
                    aria-label="report bitacora table"
                    size={hasMoreThan10Rows ? "small" : "medium"}
                  >
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              minWidth: column.minWidth,
                              py: 1.25,
                              backgroundColor: theme.palette.blueGray50.main,
                            }}
                          >
                            <Typography variant="h6">{column.label}</Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {rows.map((row) => {
                        const hasChanges = row.changes?.length > 0;
                        const isExpanded = expandedRow === row.id;
                        return (
                        <Fragment key={row.id}>
                        <TableRow
                          hover
                          onClick={hasChanges ? () => toggleRow(row.id) : undefined}
                          sx={{
                            cursor: hasChanges ? "pointer" : "default",
                            transition: "transform 0.2s ease-in-out",
                            transformOrigin: "center",
                            "& > *": { borderBottom: isExpanded ? "unset" : undefined },
                            "&:hover": {
                              transform: { xs: "none", xl: "scale(1.007)" },
                              boxShadow: theme.shadows[1],
                            },
                          }}
                        >
                          {columns.map((col) => (
                            <TableCell
                              key={col.id}
                              align={col.align}
                              sx={{ maxWidth: 300, px: 1 }}
                            >
                              {col.id === "event" && hasChanges ? (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <IconButton
                                    size="small"
                                    aria-label="toggle-changes"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleRow(row.id);
                                    }}
                                    sx={{
                                      transition: "transform 0.2s ease-in-out",
                                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                  >
                                    <ArrowDown2 size={16} color={theme.palette.primary.main} />
                                  </IconButton>
                                  <Typography variant="body2">{row[col.id]}</Typography>
                                </Stack>
                              ) : col.id === "details" ? (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  {row[col.id]?.length > 0 ? (
                                    <>
                                      {/* Method chip */}
                                      {row[col.id]
                                        .filter((action) => action.isMethod)
                                        .map((action, idx) => (
                                          <Chip
                                            key={`method-${idx}`}
                                            label={action.value}
                                            size="small"
                                            color={action.color}
                                            sx={{ fontSize: 11, height: 22 }}
                                          />
                                        ))}
                                      {/* User ID chips with special tooltip */}
                                      {row[col.id]
                                        .filter((action) => action.isUserId)
                                        .map((action, idx) => (
                                          <UserTooltipChip
                                            key={`user-${idx}`}
                                            userId={action.userId}
                                            label={`${action.label}: ${action.value}`}
                                            t={t}
                                          />
                                        ))}
                                      {/* Other details in eye icon tooltip */}
                                      {row[col.id].filter((action) => !action.isUserId && !action.isMethod).length > 0 && (
                                        <Tooltip
                                          arrow
                                          slotProps={{
                                            tooltip: {
                                              sx: {
                                                bgcolor: theme.palette.background.paper,
                                                border: `1px solid ${theme.palette.divider}`,
                                                boxShadow: theme.shadows[3],
                                                '& .MuiTooltip-arrow': {
                                                  color: theme.palette.background.paper,
                                                },
                                              },
                                            },
                                          }}
                                          title={
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ p: 0.5 }}>
                                              {row[col.id]
                                                .filter((action) => !action.isUserId && !action.isMethod)
                                                .map((action, idx) => (
                                                  <Tooltip key={idx} title={action.label} arrow placement="top">
                                                    <Chip
                                                      label={action.value}
                                                      size="small"
                                                      color={action.color}
                                                      sx={{ fontSize: 10, height: 18, cursor: 'pointer' }}
                                                    />
                                                  </Tooltip>
                                                ))}
                                            </Stack>
                                          }
                                        >
                                          <IconButton size="small">
                                            <OutlineEye size={18} color={theme.palette.primary.main} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      fontSize={12}
                                    >
                                      -
                                    </Typography>
                                  )}
                                </Stack>
                              ) : (
                                row[col.id]
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        {hasChanges && (
                          <TableRow>
                            <TableCell
                              colSpan={columns.length}
                              sx={{ py: 0, border: 0, bgcolor: alpha(theme.palette.blueGray50.main, 0.2) }}
                            >
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 2, px: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }} color="secondary">
                                    {t("Bitacora.changes")}
                                  </Typography>
                                  <Table
                                    size="small"
                                    sx={{
                                      backgroundColor: theme.palette.background.paper,
                                      borderRadius: 1,
                                      border: `1px solid ${alpha(theme.palette.blueGray50.main, 0.4)}`,
                                    }}
                                  >
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>
                                          {t("Bitacora.field")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>
                                          {t("Bitacora.before")}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, py: 1 }}>
                                          {t("Bitacora.after")}
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {row.changes.map((change, idx) => {
                                        const changeFieldName = (change.field || change.key || "")
                                          .toString()
                                          .split(".")
                                          .pop();
                                        return (
                                        <TableRow key={idx}>
                                          <TableCell sx={{ verticalAlign: "top", width: "25%" }}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: 500, fontSize: 12 }}
                                            >
                                              {change.label || change.field}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ verticalAlign: "top", width: "37.5%" }}>
                                            <ChangeValue value={change.old} fieldName={changeFieldName} />
                                          </TableCell>
                                          <TableCell sx={{ verticalAlign: "top", width: "37.5%" }}>
                                            <ChangeValue value={change.new} fieldName={changeFieldName} />
                                          </TableCell>
                                        </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                        </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>

              {totalCount > 0 && (
                <Stack
                  component="nav"
                  sx={{
                    borderTop: `1px solid ${theme.palette.blueGray50.main}`,
                    alignItems: "center",
                    justifyContent: "space-around",
                    flexFlow: "row wrap",
                    py: 2,
                    mt: 2,
                  }}
                  spacing={1}
                >
                  <Stack sx={{ flexFlow: "row nowrap" }}>
                    <Typography p={0.5} fontSize={11}>
                      {t("ReportsList.per_page")}:
                    </Typography>
                    <select
                      className="paginator__select"
                      name="limit"
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                    </select>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <EGPaginator
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                    />
                  </Stack>

                  <Typography p={0.5} fontSize={12}>
                    {`${t("ReportsList.results")}: ${
                      totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1
                    } ${t("ReportsList.to")} ${Math.min(
                      page * rowsPerPage,
                      totalCount
                    )} ${t("ReportsList.of")} ${totalCount}`}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
};
